import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import BottomSheet from 'components/_UI/BottomSheet';
import { useCart } from 'context/restaurant/CartContext';
import { useTab } from 'context/restaurant/TabContext';
import { useUser } from 'context/user/User';
import commandsApi from 'api/requests/commands';
import { Trash2, ShoppingCart, Check, Send } from 'lucide-react';
import colors from 'theme/colors';

function formatPrice(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function CartSheet({ isOpen, onClose }) {
    const { cartItems, removeItem, clearCart, cartTotal } = useCart();
    const { activeTab } = useTab();
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);

    async function handleSend() {
        setSending(true);
        setSendError(null);
        if (!slug || !activeTab?.id) {
            setSendError('Comanda não encontrada. Tente novamente.');
            setSending(false);
            return;
        }
        try {
            const items = cartItems.map(item => ({
                menu_product_id: item.product.id,
                quantity:        item.quantity,
                observation:     item.observation || null,
                option_ids:      Object.values(item.selections || {}).flat(),
            }));
            await commandsApi.addItemsBatch(slug, activeTab.id, { items });

            // Show success state briefly
            setShowSuccess(true);
            setTimeout(() => {
                clearCart();
                setShowSuccess(false);
                onClose();
            }, 1500);
        } catch {
            setSendError('Erro ao enviar pedido. Tente novamente.');
        } finally {
            setSending(false);
        }
    }

    const totalItems = cartItems.reduce((s, i) => s + i.quantity, 0);

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={() => !sending && !showSuccess && onClose()}
            title={`Pedido atual (${totalItems} ${totalItems === 1 ? 'item' : 'itens'})`}
            size="large"
        >
            {showSuccess ? (
                <SuccessState>
                    <SuccessCircle>
                        <Check size={36} color="#fff" strokeWidth={3} />
                    </SuccessCircle>
                    <SuccessTitle>Pedido enviado!</SuccessTitle>
                    <SuccessHint>A cozinha já recebeu os itens</SuccessHint>
                </SuccessState>
            ) : cartItems.length === 0 ? (
                <EmptyCart>
                    <ShoppingCart size={40} color={colors.neutralDark} />
                    <EmptyText>Nenhum item adicionado</EmptyText>
                    <EmptyHint>Selecione produtos do cardápio para adicionar</EmptyHint>
                </EmptyCart>
            ) : (
                <>
                    <ItemList>
                        {cartItems.map((item, index) => (
                            <CartItem key={index}>
                                <ItemMain>
                                    <ItemQty>{item.quantity}x</ItemQty>
                                    <ItemDetails>
                                        <ItemName>{item.product.name}</ItemName>
                                        <ItemUnit>{formatPrice(item.totalPrice)} / un</ItemUnit>
                                        {item.observation && (
                                            <ItemObs>"{item.observation}"</ItemObs>
                                        )}
                                    </ItemDetails>
                                    <ItemTotal>{formatPrice(item.totalPrice * item.quantity)}</ItemTotal>
                                </ItemMain>
                                <ItemActions>
                                    <RemoveBtn
                                        onClick={() => !sending && removeItem(index)}
                                        disabled={sending}
                                        aria-label="Remover item"
                                    >
                                        <Trash2 size={14} color={colors.danger} />
                                        <RemoveBtnText>Remover</RemoveBtnText>
                                    </RemoveBtn>
                                </ItemActions>
                            </CartItem>
                        ))}
                    </ItemList>

                    <TotalSection>
                        <TotalRow>
                            <TotalLabel>Total do pedido</TotalLabel>
                            <TotalValue>{formatPrice(cartTotal)}</TotalValue>
                        </TotalRow>
                        <TotalItemCount>
                            {totalItems} {totalItems === 1 ? 'item' : 'itens'}
                        </TotalItemCount>
                    </TotalSection>

                    {sendError && (
                        <SendError>{sendError}</SendError>
                    )}

                    <SendBtn onClick={handleSend} disabled={sending}>
                        {sending ? (
                            <>
                                <SendSpinner />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Send size={18} />
                                Enviar para a cozinha
                            </>
                        )}
                    </SendBtn>
                </>
            )}
        </BottomSheet>
    );
}

/* ───── Animations ───── */

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const successPop = keyframes`
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.15); }
    100% { transform: scale(1); opacity: 1; }
`;

const successFade = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

/* ───── Success State ───── */

const SuccessState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 40px 20px;
    gap: 16px;
`;

const SuccessCircle = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${colors.success};
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${successPop} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 0 0 8px ${colors.success}18, 0 8px 24px ${colors.success}30;
`;

const SuccessTitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
    animation: ${successFade} 0.3s ease 0.15s both;
`;

const SuccessHint = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0;
    animation: ${successFade} 0.3s ease 0.25s both;
`;

/* ───── Empty State ───── */

const EmptyCart = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 20px;
    text-align: center;
    gap: 12px;
`;

const EmptyText = styled.p`font-weight: 700; font-size: 16px; color: ${colors.nightDark}; margin: 0;`;
const EmptyHint = styled.p`font-size: 13px; color: ${colors.nightLight}; margin: 0;`;

/* ───── Item List ───── */

const ItemList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CartItem = styled.div`
    background: #f5f5f7;
    border-radius: 12px;
    overflow: hidden;
`;

const ItemMain = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
`;

const ItemQty = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    min-width: 28px;
    text-align: center;
`;

const ItemDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

const ItemName = styled.span`
    font-weight: 600;
    font-size: 14px;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemUnit = styled.span`font-size: 12px; color: ${colors.nightLight};`;

const ItemObs = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    font-style: italic;
    margin-top: 2px;
`;

const ItemTotal = styled.span`
    font-weight: 700;
    font-size: 14px;
    color: ${colors.nightDark};
    flex-shrink: 0;
`;

const ItemActions = styled.div`
    display: flex;
    border-top: 1px solid ${colors.neutralDark};
`;

const RemoveBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 10px;
    background: transparent;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.danger}08; }
`;

const RemoveBtnText = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.danger};
`;

/* ───── Total Section ───── */

const TotalSection = styled.div`
    margin-top: 12px;
    padding: 16px 0 4px;
    border-top: 2px solid ${colors.nightDark};
`;

const TotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TotalLabel = styled.span`font-weight: 700; font-size: 16px; color: ${colors.nightDark};`;
const TotalValue = styled.span`font-weight: 700; font-size: 20px; color: ${colors.occasionalPurple};`;

const TotalItemCount = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    margin-top: 4px;
    display: block;
`;

/* ───── Send ───── */

const SendError = styled.div`
    font-size: 13px;
    color: ${colors.danger};
    text-align: center;
    padding: 10px 14px;
    background: ${colors.danger}0A;
    border-radius: 10px;
    margin-top: 12px;
`;

const SendBtn = styled.button`
    width: 100%;
    padding: 15px;
    border-radius: 12px;
    background: ${colors.nightDark};
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    margin-top: 10px;
    transition: opacity 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    &:hover { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SendSpinner = styled.div`
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
